import React, { useEffect, useRef, useState } from "react";
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import Icon from "components/AppIcon";

// Animated counter hook
function useAnimatedValue(target, duration = 600) {
  const [display, setDisplay] = useState(target);
  const prevRef = useRef(target);
  const rafRef = useRef(null);

  useEffect(() => {
    const from = prevRef?.current;
    const to = target;
    if (from === to) return;
    const start = performance.now();
    const animate = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(from + (to - from) * eased);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        setDisplay(to);
        prevRef.current = to;
      }
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef?.current);
  }, [target, duration]);

  return display;
}

const NetWorthCard = ({ netWorth, change, changePercent, trendData, isLive, onRefresh }) => {
  const safeNetWorth = typeof netWorth === 'number' && !isNaN(netWorth) ? netWorth : 0;
  const safeChange = typeof change === 'number' && !isNaN(change) ? change : 0;
  const safeChangePercent = typeof changePercent === 'number' && !isNaN(changePercent) ? changePercent : 0;
  const safeTrendData = Array.isArray(trendData) ? trendData : [];
  const isPositive = safeChange >= 0;

  // Animated display value
  const animatedNetWorth = useAnimatedValue(safeNetWorth);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border border-border rounded px-2 py-1">
          <span className="font-caption text-xs text-primary">
            ${payload?.[0]?.value?.toLocaleString("en-US", { minimumFractionDigits: 2 })}
          </span>
        </div>
      );
    }
    return null;
  };

  return (
    <div
      className="rounded-xl border border-border p-4 md:p-6 relative"
      style={{ background: "var(--color-card)", boxShadow: "var(--glow-sm)" }}
    >
      {/* Live indicator */}
      {isLive && (
        <div className="absolute top-3 right-3 flex items-center gap-1.5">
          <span
            className="w-1.5 h-1.5 rounded-full animate-pulse"
            style={{ background: 'var(--color-success)' }}
          />
          <span className="font-caption text-xs" style={{ color: 'var(--color-success)' }}>Live</span>
          {onRefresh && (
            <button
              onClick={onRefresh}
              className="ml-1 opacity-50 hover:opacity-100 transition-opacity"
              title="Manual refresh"
            >
              <Icon name="RefreshCw" size={11} color="var(--color-muted-foreground)" />
            </button>
          )}
        </div>
      )}

      <div className="flex items-start justify-between mb-2">
        <div>
          <p className="font-caption text-xs text-muted-foreground uppercase tracking-widest mb-1">
            Net Worth
          </p>
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground tabular-nums">
            ${animatedNetWorth?.toLocaleString("en-US", { minimumFractionDigits: 2 })}
          </h2>
        </div>
        <div
          className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-caption font-medium mt-6 ${
            isPositive
              ? "bg-success/10 text-success border border-success/20" : "bg-error/10 text-error border border-error/20"
          }`}
        >
          <Icon
            name={isPositive ? "TrendingUp" : "TrendingDown"}
            size={12}
            color={isPositive ? "var(--color-success)" : "var(--color-error)"}
          />
          {isPositive ? "+" : ""}
          {safeChangePercent?.toFixed(2)}%
        </div>
      </div>
      <p
        className={`font-caption text-sm mb-4 ${
          isPositive ? "text-success" : "text-error"
        }`}
      >
        {isPositive ? "+" : ""}${safeChange?.toLocaleString("en-US", { minimumFractionDigits: 2 })} 7-day change
      </p>
      <div className="h-20 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={safeTrendData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="netWorthGrad" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor={isPositive ? "var(--color-success)" : "var(--color-error)"}
                  stopOpacity={0.3}
                />
                <stop
                  offset="95%"
                  stopColor={isPositive ? "var(--color-success)" : "var(--color-error)"}
                  stopOpacity={0}
                />
              </linearGradient>
            </defs>
            <XAxis dataKey="label" hide />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="value"
              stroke={isPositive ? "var(--color-success)" : "var(--color-error)"}
              strokeWidth={2}
              fill="url(#netWorthGrad)"
              dot={false}
              isAnimationActive={true}
              animationDuration={400}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default NetWorthCard;